import type { CSSProperties } from "react";

import type {
    BlockThemeOverride,
    LomdaTheme,
    LomdaV3FontWeights,
    LomdaV3LineHeights,
} from "@/lib/utils/lomdaV3/types";
import type { LomdaThemeInput } from "@/lib/utils/lomdaV3/themeSchema";
import { BUILT_IN_FONT_WEIGHTS } from "@/features/brandKits/lib/builtInFonts";

export type { LomdaTheme, LomdaThemeInput, LomdaV3LineHeights };

/** Keys a single block can override (subset of the theme colors). */
export const BLOCK_OVERRIDE_KEYS: {
    key: keyof BlockThemeOverride;
    labelKey: string;
}[] = [
    { key: "background", labelKey: "lomda-v3-editor.theme.background" },
    { key: "card", labelKey: "lomda-v3-editor.theme.card" },
    { key: "foreground", labelKey: "lomda-v3-editor.theme.foreground" },
    { key: "primary", labelKey: "lomda-v3-editor.theme.primary" },
];

/** Section gap — vertical spacing (px) between top-level sections on a page. */
export const DEFAULT_BLOCK_GAP = 40;

/** Grid gap — spacing (px) between items inside a grid (e.g. cards). */
export const DEFAULT_GRID_GAP = 24;

/** Inner gap — spacing (px) inside one section (title/paragraph/button/etc.). */
export const DEFAULT_INNER_GAP = 16;

/**
 * Internal shadow system — exactly 4 values, derived from the Figma effect
 * styles (`shadow` / `card hover` / `active`). Deliberately NOT user-editable
 * and not exposed in the theme panel, unlike colors + typography.
 */
export const LOMDA_SHADOWS = {
    /** Figma `shadow` — surfaces at rest (second-level topbar, cards). */
    card: "0 16px 30px #F3F7FF",
    hover: "0 8px 16px rgb(153 187 255 / 0.12)",
    active: "0 0 0 4px hsl(var(--lomda-border-active) / 0.25)",
    popover: "0 4px 10px -1px rgb(0 0 0 / 0.08)",
} as const;

/** Shadow tokens as CSS vars (merged into the scope vars). */
export function buildShadowVars(): CSSProperties {
    return {
        "--lomda-shadow-card": LOMDA_SHADOWS.card,
        "--lomda-shadow-hover": LOMDA_SHADOWS.hover,
        "--lomda-shadow-active": LOMDA_SHADOWS.active,
        "--lomda-shadow-popover": LOMDA_SHADOWS.popover,
    } as CSSProperties;
}

/**
 * The value `cardForeground` carries when the author has never picked one.
 * It sat in the data model long before it was editable, so EVERY lomda saved
 * until now stores exactly this — which is why it reads as "not set".
 */
export const UNSET_CARD_FOREGROUND = "#161619";

/** "Text on cards" follows "Text" until the author gives it its own colour. */
export function resolveCardForeground(theme: LomdaThemeInput): string {
    const c = theme.cardForeground;
    if (!c || c.toLowerCase() === UNSET_CARD_FOREGROUND.toLowerCase())
        return theme.foreground;
    return c;
}

/** Default = the "yedalabs blue" theme from the Figma prototype. */
export const DEFAULT_THEME: LomdaThemeInput = {
    background: "#F6F9FF",
    foreground: "#161619",
    primary: "#0A59EB",
    primaryForeground: "#ffffff",
    card: "#FFFFFF",
    cardForeground: UNSET_CARD_FOREGROUND,
    muted: "#F5F7F8",
    mutedForeground: "#8B8EA4",
    border: "#E8EAF5",
    borderDashed: "#BEC2DD",
    icons: "#BEC2DD",
    secondary: "#0A59EB",
    borderActive: "#76A4F9",
    hover: "#E8EAF5",
    success: "#01A22A",
    destructive: "#E51921",
    radius: 16,
    fontBody: "Rubik",
    fontHeading: "Rubik",
    sizeH1: 40,
    sizeH2: 30,
    sizeH3: 28,
    sizeP: 16,
    sizeH4: 24,
    sizeH5: 20,
    sizeH6: 18,
    sizeLabel: 14,
    sizeSmall: 14,
    sectionGap: DEFAULT_BLOCK_GAP,
    gridGap: DEFAULT_GRID_GAP,
    innerGap: DEFAULT_INNER_GAP,
};

export const THEME_COLOR_KEYS: {
    key: keyof LomdaTheme;
    labelKey: string;
    /** Shows the colour the content actually renders with, when that is
     *  derived rather than stored. */
    resolve?: (theme: LomdaThemeInput) => string;
}[] = [
    // Order + set mirrors the Figma "theme system" table. primaryForeground is
    // kept in the data model (computed default) but no longer exposed here.
    { key: "card", labelKey: "lomda-v3-editor.theme.card" },
    { key: "background", labelKey: "lomda-v3-editor.theme.background" },
    { key: "border", labelKey: "lomda-v3-editor.theme.border" },
    { key: "borderDashed", labelKey: "lomda-v3-editor.theme.border-dashed" },
    { key: "foreground", labelKey: "lomda-v3-editor.theme.foreground" },
    {
        key: "cardForeground",
        labelKey: "lomda-v3-editor.theme.card-fg",
        resolve: resolveCardForeground,
    },
    { key: "mutedForeground", labelKey: "lomda-v3-editor.theme.muted-fg" },
    { key: "muted", labelKey: "lomda-v3-editor.theme.muted" },
    { key: "icons", labelKey: "lomda-v3-editor.theme.icons" },
    { key: "primary", labelKey: "lomda-v3-editor.theme.primary" },
    { key: "secondary", labelKey: "lomda-v3-editor.theme.secondary" },
    { key: "borderActive", labelKey: "lomda-v3-editor.theme.border-active" },
    { key: "hover", labelKey: "lomda-v3-editor.theme.hover" },
    { key: "success", labelKey: "lomda-v3-editor.theme.success" },
    { key: "destructive", labelKey: "lomda-v3-editor.theme.destructive" },
];

export const THEME_SIZE_KEYS: {
    key: keyof LomdaThemeInput;
    labelKey: string;
}[] = [
    { key: "sizeH1", labelKey: "lomda-v3-editor.theme.size-h1" },
    { key: "sizeH2", labelKey: "lomda-v3-editor.theme.size-h2" },
    { key: "sizeH3", labelKey: "lomda-v3-editor.theme.size-h3" },
    { key: "sizeH4", labelKey: "lomda-v3-editor.theme.size-h4" },
    { key: "sizeH5", labelKey: "lomda-v3-editor.theme.size-h5" },
    { key: "sizeH6", labelKey: "lomda-v3-editor.theme.size-h6" },
    { key: "sizeLabel", labelKey: "lomda-v3-editor.theme.size-label" },
    { key: "sizeP", labelKey: "lomda-v3-editor.theme.size-p" },
    { key: "sizeSmall", labelKey: "lomda-v3-editor.theme.size-small" },
];

/** Spacing tokens exposed in the settings bar + brand kit (px). */
export const THEME_SPACING_KEYS: {
    key: keyof LomdaThemeInput;
    labelKey: string;
}[] = [
    { key: "sectionGap", labelKey: "lomda-v3-editor.theme.section-gap" },
    { key: "gridGap", labelKey: "lomda-v3-editor.theme.grid-gap" },
    { key: "innerGap", labelKey: "lomda-v3-editor.theme.inner-gap" },
];

/** Default font weight per text preset (mirrors the rich-text CSS fallbacks). */
export const DEFAULT_FONT_WEIGHTS: Required<LomdaV3FontWeights> = {
    h1: 600,
    h2: 600,
    h3: 600,
    h4: 600,
    h5: 600,
    h6: 400,
    label: 600,
    p: 400,
    small: 400,
};

export const FONT_WEIGHT_OPTIONS = [
    100, 200, 300, 400, 500, 600, 700, 800, 900,
];

/** The weights a font really has. Uploaded fonts keep the full list until #20. */
export function fontWeightOptions(family: string | undefined): readonly number[] {
    return family && Object.hasOwn(BUILT_IN_FONT_WEIGHTS, family)
        ? BUILT_IN_FONT_WEIGHTS[family]
        : FONT_WEIGHT_OPTIONS;
}

/** The weight a font renders for a stored weight it doesn't have. */
export function nearestWeight(weight: number, options: readonly number[]): number {
    return options.reduce((best, w) =>
        Math.abs(w - weight) < Math.abs(best - weight) ? w : best
    );
}

/** Default line height per text preset, as a percentage (116 = 116%). */
export const DEFAULT_LINE_HEIGHTS: Required<LomdaV3LineHeights> = {
    h1: 116,
    h2: 116,
    h3: 132,
    h4: 132,
    h5: 132,
    h6: 150,
    label: 150,
    p: 150,
    small: 150,
};

/**
 * Ordered text presets for the typography table (Figma "theme system"). Each
 * row edits a font size + weight + line height and shows a live preview label.
 * `heading` = the titles group (H1–H6 + Label): the row previews in, and offers
 * the weights of, the titles font; the others use the body font.
 */
export const THEME_TYPOGRAPHY_PRESETS: {
    key: keyof LomdaV3FontWeights;
    sizeKey: keyof LomdaThemeInput;
    labelKey: string;
    heading: boolean;
}[] = [
    { key: "h1", sizeKey: "sizeH1", labelKey: "lomda-v3-editor.theme.preset-h1", heading: true },
    { key: "h2", sizeKey: "sizeH2", labelKey: "lomda-v3-editor.theme.preset-h2", heading: true },
    { key: "h3", sizeKey: "sizeH3", labelKey: "lomda-v3-editor.theme.preset-h3", heading: true },
    { key: "h4", sizeKey: "sizeH4", labelKey: "lomda-v3-editor.theme.preset-h4", heading: true },
    { key: "h5", sizeKey: "sizeH5", labelKey: "lomda-v3-editor.theme.preset-h5", heading: true },
    { key: "h6", sizeKey: "sizeH6", labelKey: "lomda-v3-editor.theme.preset-h6", heading: true },
    { key: "label", sizeKey: "sizeLabel", labelKey: "lomda-v3-editor.theme.preset-label", heading: true },
    { key: "p", sizeKey: "sizeP", labelKey: "lomda-v3-editor.theme.preset-p", heading: false },
    { key: "small", sizeKey: "sizeSmall", labelKey: "lomda-v3-editor.theme.preset-small", heading: false },
];

/** The titles group (STANDARDS §4), keyed like a line's tag ("h1"…"h6", "label"). */
export const TITLES_PRESETS: ReadonlySet<string> = new Set(
    THEME_TYPOGRAPHY_PRESETS.filter((p) => p.heading).map((p) => p.key)
);

export const THEME_WEIGHT_KEYS: {
    key: keyof LomdaV3FontWeights;
    labelKey: string;
}[] = [
    { key: "h1", labelKey: "lomda-v3-editor.theme.weight-h1" },
    { key: "h2", labelKey: "lomda-v3-editor.theme.weight-h2" },
    { key: "h3", labelKey: "lomda-v3-editor.theme.weight-h3" },
    { key: "h4", labelKey: "lomda-v3-editor.theme.weight-h4" },
    { key: "h5", labelKey: "lomda-v3-editor.theme.weight-h5" },
    { key: "h6", labelKey: "lomda-v3-editor.theme.weight-h6" },
    { key: "label", labelKey: "lomda-v3-editor.theme.weight-label" },
    { key: "p", labelKey: "lomda-v3-editor.theme.weight-p" },
    { key: "small", labelKey: "lomda-v3-editor.theme.weight-small" },
];

function hexChannels(hex: string): [number, number, number] | null {
    let h = (hex ?? "").replace("#", "").trim();
    if (h.length === 3)
        h = h
            .split("")
            .map((c) => c + c)
            .join("");
    if (!/^[0-9a-f]{6}$/i.test(h)) return null;
    return [
        parseInt(h.slice(0, 2), 16),
        parseInt(h.slice(2, 4), 16),
        parseInt(h.slice(4, 6), 16),
    ];
}

/**
 * Blend two hex colors — `ratio` is how much of `a` survives (1 = all `a`).
 * Used to derive the chart palette from the theme, where charts need several
 * distinguishable colors but the theme only defines a couple of brand ones.
 * Returns `a` unchanged if either color isn't parseable hex.
 */
export function mixHex(a: string, b: string, ratio: number): string {
    const ca = hexChannels(a);
    const cb = hexChannels(b);
    if (!ca || !cb) return a;
    const r = Math.max(0, Math.min(1, ratio));
    const ch = ca.map((v, i) =>
        Math.round(v * r + cb[i] * (1 - r))
            .toString(16)
            .padStart(2, "0")
    );
    return `#${ch.join("")}`;
}

/** "H S% L% / alpha" for an `hsl(var(--x))` token; null when `hex` isn't hex. */
export const hexToHslAlpha = (hex: string, alpha: number): string | null =>
    hexChannels(hex) ? `${hexToHsl(hex)} / ${alpha}` : null;

/** Convert a hex color to the "H S% L%" channel string shadcn tokens expect. */
export function hexToHsl(hex: string): string {
    let h = hex.replace("#", "").trim();
    if (h.length === 3)
        h = h
            .split("")
            .map((c) => c + c)
            .join("");
    const r = parseInt(h.slice(0, 2), 16) / 255;
    const g = parseInt(h.slice(2, 4), 16) / 255;
    const b = parseInt(h.slice(4, 6), 16) / 255;
    if ([r, g, b].some((v) => Number.isNaN(v))) return "0 0% 0%";
    const max = Math.max(r, g, b);
    const min = Math.min(r, g, b);
    const l = (max + min) / 2;
    let hue = 0;
    let s = 0;
    const d = max - min;
    if (d !== 0) {
        s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
        switch (max) {
            case r:
                hue = (g - b) / d + (g < b ? 6 : 0);
                break;
            case g:
                hue = (b - r) / d + 2;
                break;
            default:
                hue = (r - g) / d + 4;
        }
        hue /= 6;
    }
    return `${Math.round(hue * 360)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`;
}

/**
 * Build the CSS variables for a theme. Overrides the shadcn tokens (so existing
 * `bg-card` / `bg-primary` / `text-foreground` / `border` utilities reflect the
 * theme) plus lomda typography/radius vars consumed by the rich-text CSS.
 */
export function buildThemeVars(theme: LomdaThemeInput): CSSProperties {
    const bg = hexToHsl(theme.background);
    const fg = hexToHsl(theme.foreground);
    const primary = hexToHsl(theme.primary);
    const primaryFg = hexToHsl(theme.primaryForeground);
    const card = hexToHsl(theme.card);
    const cardFg = hexToHsl(resolveCardForeground(theme));
    const muted = hexToHsl(theme.muted);
    const mutedFg = hexToHsl(theme.mutedForeground);
    const border = hexToHsl(theme.border);
    const borderDashed = hexToHsl(theme.borderDashed ?? "#BEC2DD");
    const icons = hexToHsl(theme.icons ?? "#BEC2DD");
    const secondary = hexToHsl(theme.secondary ?? "#0A59EB");
    const borderActive = hexToHsl(theme.borderActive ?? "#76A4F9");
    const hover = hexToHsl(theme.hover ?? "#E8EAF5");
    const success = hexToHsl(theme.success ?? "#01A22A");
    const destructive = hexToHsl(theme.destructive ?? "#E51921");

    return {
        "--background": bg,
        "--foreground": fg,
        "--card": card,
        "--card-foreground": cardFg,
        "--popover": card,
        "--popover-foreground": cardFg,
        "--primary": primary,
        "--primary-foreground": primaryFg,
        "--secondary": muted,
        "--secondary-foreground": fg,
        "--muted": muted,
        "--muted-foreground": mutedFg,
        "--accent": muted,
        // Hover/selected text for shadcn menus: those are popovers, i.e. card
        // surfaces, so it follows "Text on cards", not "Text".
        "--accent-foreground": cardFg,
        "--border": border,
        "--input": border,
        "--ring": primary,
        "--destructive": destructive,
        "--destructive-foreground": "0 0% 100%",
        // Lomda-only color namespace (Figma theme system). Separate from the
        // app-wide shadcn tokens above so lomda styling never leaks app-wide.
        "--lomda-card": card,
        "--lomda-background": bg,
        "--lomda-text": fg,
        // The two text colors, kept un-remapped so a surface can point
        // `--lomda-text` / `--foreground` at either one (see the
        // `lomda-v3-card-text` / `lomda-v3-page-text` classes in richText.css).
        "--lomda-page-text": fg,
        "--lomda-card-text": cardFg,
        "--lomda-muted-text": mutedFg,
        "--lomda-muted": muted,
        "--lomda-primary": primary,
        "--lomda-border": border,
        "--lomda-border-dashed": borderDashed,
        "--lomda-icons": icons,
        "--lomda-secondary": secondary,
        "--lomda-border-active": borderActive,
        "--lomda-hover": hover,
        "--lomda-success": success,
        "--lomda-destructive": destructive,
        "--radius": `${theme.radius}px`,
        "--lomda-font-body": theme.fontBody,
        "--lomda-font-titles": theme.fontHeading,
        "--lomda-size-h1": `${theme.sizeH1}px`,
        "--lomda-size-h2": `${theme.sizeH2}px`,
        "--lomda-size-h3": `${theme.sizeH3}px`,
        "--lomda-size-h4": `${theme.sizeH4 ?? DEFAULT_THEME.sizeH4}px`,
        "--lomda-size-h5": `${theme.sizeH5 ?? DEFAULT_THEME.sizeH5}px`,
        "--lomda-size-h6": `${theme.sizeH6 ?? DEFAULT_THEME.sizeH6}px`,
        "--lomda-size-label": `${theme.sizeLabel ?? DEFAULT_THEME.sizeLabel}px`,
        "--lomda-size-p": `${theme.sizeP}px`,
        "--lomda-size-small": `${theme.sizeSmall ?? DEFAULT_THEME.sizeSmall}px`,
        "--lomda-inner-gap": `${theme.innerGap ?? DEFAULT_INNER_GAP}px`,
        ...buildLineHeightVars(theme.lineHeights),
    } as CSSProperties;
}

/** So text without a lomda class (player chrome, chart ticks, inputs) never
 *  falls back to the platform font. Spread after the theme vars it reads. */
export const CONTENT_ROOT_FONT: CSSProperties = {
    fontFamily: "var(--lomda-font-body), sans-serif",
};

/** CSS vars for per-preset line heights (percent → unitless line-height). */
export function buildLineHeightVars(
    lineHeights?: LomdaV3LineHeights
): CSSProperties {
    const v: Record<string, string> = {};
    for (const key of Object.keys(DEFAULT_LINE_HEIGHTS) as (keyof LomdaV3LineHeights)[]) {
        const pct = lineHeights?.[key] ?? DEFAULT_LINE_HEIGHTS[key];
        v[`--lomda-line-${key}`] = String(pct / 100);
    }
    return v as CSSProperties;
}

/** CSS vars for per-preset font weights (consumed by the rich-text CSS). */
export function buildFontWeightVars(
    weights?: LomdaV3FontWeights
): CSSProperties {
    return {
        "--lomda-weight-h1": String(weights?.h1 ?? DEFAULT_FONT_WEIGHTS.h1),
        "--lomda-weight-h2": String(weights?.h2 ?? DEFAULT_FONT_WEIGHTS.h2),
        "--lomda-weight-h3": String(weights?.h3 ?? DEFAULT_FONT_WEIGHTS.h3),
        "--lomda-weight-h4": String(weights?.h4 ?? DEFAULT_FONT_WEIGHTS.h4),
        "--lomda-weight-h5": String(weights?.h5 ?? DEFAULT_FONT_WEIGHTS.h5),
        "--lomda-weight-h6": String(weights?.h6 ?? DEFAULT_FONT_WEIGHTS.h6),
        "--lomda-weight-label": String(
            weights?.label ?? DEFAULT_FONT_WEIGHTS.label
        ),
        "--lomda-weight-p": String(weights?.p ?? DEFAULT_FONT_WEIGHTS.p),
        "--lomda-weight-small": String(
            weights?.small ?? DEFAULT_FONT_WEIGHTS.small
        ),
    } as CSSProperties;
}

/** CSS vars for a single block's color override (only the keys that are set). */
export function buildBlockOverrideVars(o: BlockThemeOverride): CSSProperties {
    const v: Record<string, string> = {};
    if (o.background) v["--background"] = hexToHsl(o.background);
    if (o.foreground) {
        v["--foreground"] = hexToHsl(o.foreground);
        v["--lomda-page-text"] = v["--foreground"];
    }
    if (o.primary) v["--primary"] = hexToHsl(o.primary);
    if (o.primaryForeground)
        v["--primary-foreground"] = hexToHsl(o.primaryForeground);
    if (o.card) {
        v["--card"] = hexToHsl(o.card);
        v["--popover"] = hexToHsl(o.card);
        // Card surfaces read the lomda namespace, so a per-block card colour
        // has to land there too or `lomda-v3-card-surface` ignores it.
        v["--lomda-card"] = v["--card"];
    }
    if (o.cardForeground) {
        v["--card-foreground"] = hexToHsl(o.cardForeground);
        v["--lomda-card-text"] = v["--card-foreground"];
    }
    return v as CSSProperties;
}
